import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image
} from '@react-pdf/renderer';

const INK = '#0B1220';
const INK_2 = '#334155';
const INK_3 = '#64748B';
const INK_4 = '#94A3B8';
const RULE = '#E2E8F0';
const RULE_STRONG = '#CBD5E1';
const ACCENT = '#2563EB';
const BG_SOFT = '#F8FAFC';

const SANS = 'Helvetica';
const MONO = 'Courier';

const styles = StyleSheet.create({
  page: {
    fontFamily: SANS,
    fontSize: 9,
    color: INK,
    backgroundColor: '#FFFFFF',
    paddingTop: 51,
    paddingBottom: 62,
    paddingHorizontal: 45,
    position: 'relative',
  },
  pageMiddle: {
    paddingTop: 40,
  },
  head: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    borderBottomWidth: 1.5,
    borderBottomColor: INK,
    borderBottomStyle: 'solid',
    paddingBottom: 17,
  },
  headCompact: {
    paddingBottom: 11,
    borderBottomWidth: 1,
    borderBottomColor: RULE,
  },
  docId: {
    flexDirection: 'column',
  },
  docLabel: {
    fontFamily: MONO,
    fontSize: 8,
    letterSpacing: 1.5,
    color: INK_3,
    marginBottom: 6,
  },
  docBig: {
    fontFamily: SANS,
    fontWeight: "normal",
    fontSize: 48,
    letterSpacing: -1.5,
    color: INK,
    lineHeight: 1,
  },
  docBigCompact: {
    fontFamily: SANS,
    fontWeight: "normal",
    fontSize: 20,
    letterSpacing: -0.4,
    color: INK,
    lineHeight: 1,
  },
  docNumRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 6,
  },
  docBullet: {
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: ACCENT,
    marginRight: 6,
  },
  docNum: {
    fontFamily: MONO,
    fontSize: 11,
    color: ACCENT,
  },
  logo: {
    height: 56,
    width: 56,
    objectFit: 'contain',
  },
  meta: {
    flexDirection: 'row',
    marginTop: 22,
    borderTopWidth: 1,
    borderTopColor: RULE,
    borderBottomWidth: 1,
    borderBottomColor: RULE,
  },
  metaCell: {
    flex: 1,
    paddingVertical: 11,
    paddingHorizontal: 11,
    borderRightWidth: 1,
    borderRightColor: RULE,
    borderRightStyle: 'solid',
  },
  metaCellFirst: {
    paddingLeft: 0,
  },
  metaCellLast: {
    borderRightWidth: 0,
    paddingRight: 0,
  },
  metaK: {
    fontFamily: MONO,
    fontSize: 7.5,
    letterSpacing: 1,
    color: INK_3,
    textTransform: 'uppercase',
    marginBottom: 5,
  },
  metaV: {
    fontFamily: MONO,
    fontSize: 10,
    color: INK,
  },
  parties: {
    flexDirection: 'row',
    marginTop: 22,
    gap: 28,
  },
  party: {
    flex: 1,
  },
  partyTag: {
    fontFamily: MONO,
    fontSize: 7.5,
    letterSpacing: 1.2,
    color: INK_3,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  partyName: {
    fontFamily: SANS,
    fontSize: 12,
    fontWeight: "normal",
    color: INK,
    letterSpacing: -0.1,
    marginBottom: 5,
  },
  partyLine: {
    fontFamily: MONO,
    fontSize: 9,
    color: INK_2,
    lineHeight: 1.55,
  },
  tableHeader: {
    flexDirection: 'row',
    marginTop: 28,
    borderBottomWidth: 1,
    borderBottomColor: INK,
    borderBottomStyle: 'solid',
    paddingVertical: 8,
    paddingHorizontal: 0,
  },
  tableHeaderMiddle: {
    marginTop: 17,
  },
  th: {
    fontFamily: MONO,
    fontSize: 7.5,
    letterSpacing: 1,
    color: INK_3,
    textTransform: 'uppercase',
    fontWeight: "normal",
  },
  thIdx: {
    width: 28,
  },
  thDesc: {
    flex: 1,
  },
  thQty: {
    width: 50,
    textAlign: 'right',
  },
  thPrice: {
    width: 68,
    textAlign: 'right',
  },
  thAmount: {
    width: 74,
    textAlign: 'right',
  },
  row: {
    flexDirection: 'row',
    paddingVertical: 11,
    borderBottomWidth: 1,
    borderBottomColor: RULE,
    borderBottomStyle: 'solid',
  },
  tdIdx: {
    width: 28,
    fontFamily: MONO,
    fontSize: 9,
    color: INK_4,
  },
  tdDesc: {
    flex: 1,
    paddingRight: 10,
  },
  tdTitle: {
    fontFamily: MONO,
    fontSize: 10,
    fontWeight: "normal",
    color: INK,
    marginBottom: 3,
  },
  tdSub: {
    fontFamily: MONO,
    fontSize: 9,
    color: INK_2,
    lineHeight: 1.5,
  },
  tdQty: {
    width: 50,
    textAlign: 'right',
    fontFamily: MONO,
    fontSize: 9.5,
    color: INK,
  },
  tdQtyUnit: {
    color: INK_4,
  },
  tdPrice: {
    width: 68,
    textAlign: 'right',
    fontFamily: MONO,
    fontSize: 9.5,
    color: INK,
  },
  tdAmount: {
    width: 74,
    textAlign: 'right',
    fontFamily: MONO,
    fontSize: 9.5,
    color: INK,
    fontWeight: "normal",
  },
  carryReport: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomStyle: 'dashed',
    borderBottomColor: RULE_STRONG,
  },
  carryLabel: {
    fontFamily: MONO,
    fontSize: 8.5,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: INK_3,
  },
  carryValue: {
    fontFamily: MONO,
    fontSize: 9.5,
    color: INK,
  },
  carryBottom: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 11,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopStyle: 'dashed',
    borderTopColor: RULE_STRONG,
  },
  totals: {
    flexDirection: 'row',
    marginTop: 22,
    gap: 28,
  },
  totalsLeft: {
    flex: 1,
  },
  totalsNotesTag: {
    fontFamily: MONO,
    fontSize: 8,
    letterSpacing: 1.2,
    color: INK_3,
    textTransform: 'uppercase',
    marginBottom: 6,
  },
  totalsNotes: {
    fontFamily: MONO,
    fontSize: 9,
    color: INK_3,
    lineHeight: 1.6,
  },
  totalsRight: {
    width: 200,
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 7,
    borderBottomWidth: 1,
    borderBottomColor: RULE,
    borderBottomStyle: 'solid',
  },
  totalsK: {
    fontFamily: MONO,
    fontSize: 10,
    color: INK_3,
  },
  totalsV: {
    fontFamily: MONO,
    fontSize: 10,
    color: INK,
  },
  totalsGrand: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    paddingTop: 11,
    paddingBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: INK,
    borderBottomStyle: 'solid',
  },
  totalsGrandK: {
    fontFamily: SANS,
    fontSize: 9,
    letterSpacing: 1.5,
    color: INK,
    textTransform: 'uppercase',
    fontWeight: "normal",
  },
  totalsGrandV: {
    fontFamily: SANS,
    fontSize: 20,
    fontWeight: "bold",
    color: ACCENT,
    letterSpacing: -0.5,
  },
  payment: {
    marginTop: 22,
    padding: 17,
    borderWidth: 1,
    borderColor: RULE,
    borderStyle: 'solid',
    backgroundColor: BG_SOFT,
  },
  paymentTag: {
    fontFamily: MONO,
    fontSize: 8,
    letterSpacing: 1.4,
    color: INK_3,
    textTransform: 'uppercase',
    marginBottom: 9,
  },
  paymentRow: {
    flexDirection: 'row',
    paddingVertical: 3,
  },
  paymentK: {
    fontFamily: MONO,
    fontSize: 9.5,
    color: INK_3,
    width: 100,
    paddingRight: 12,
  },
  paymentV: {
    fontFamily: MONO,
    fontSize: 9.5,
    color: INK,
    flex: 1,
  },
  footer: {
    position: 'absolute',
    bottom: 28,
    left: 45,
    right: 45,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: RULE,
    borderTopStyle: 'solid',
    gap: 10,
  },
  footerLegal: {
    fontFamily: MONO,
    fontSize: 6.5,
    color: INK_3,
    letterSpacing: 0.3,
    lineHeight: 1.55,
    flex: 1,
  },
  footerPage: {
    fontFamily: MONO,
    fontSize: 6.5,
    color: INK_3,
    letterSpacing: 0.3,
  },
  validity: {
    marginTop: 22,
    padding: 17,
    borderWidth: 1,
    borderColor: RULE,
    borderStyle: 'solid',
    backgroundColor: BG_SOFT,
  },
});

const parsePrice = (price) => {
  if (typeof price === 'number') return price;
  if (typeof price === 'string') return parseFloat(price.replace(',', '.'));
  return 0;
};

const parseDate = (dateField) => {
  if (!dateField) return new Date();
  if (dateField.seconds) return new Date(dateField.seconds * 1000);
  return new Date(dateField);
};

const fmt = (n) =>
  new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(n)
    .replace(/[\u202F\u00A0]/g, ' ');

const formatDate = (d) =>
  parseDate(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

const formatMonthYear = (d) =>
  parseDate(d).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });

/**
 * Libellé d'une ligne : les factures historiques stockent `title`, le
 * formulaire de saisie stocke `product`. On accepte les deux.
 */
const itemLabel = (item) =>
  ((item && (item.title || item.product || item.designation || item.name)) || '').trim();

const estimateRowHeightPt = (item) => {
  const title = itemLabel(item);
  const desc = item.description || '';
  const titleCharsPerLine = 55;
  const descCharsPerLine = 65;
  const titleLines = Math.max(1, Math.ceil(title.length / titleCharsPerLine));
  const descLines = desc ? Math.ceil(desc.length / descCharsPerLine) : 0;
  return 22 + (titleLines - 1) * 13 + descLines * 13;
};

const paginateItems = (items) => {
  const FIRST_CAP = 180;
  const MIDDLE_CAP = 620;
  const LAST_BLOCK = 260;

  const pages = [];
  let current = [];
  let currentH = 0;
  let cap = FIRST_CAP;

  for (let i = 0; i < items.length; i++) {
    const h = estimateRowHeightPt(items[i]);
    if (currentH + h > cap && current.length > 0) {
      pages.push(current);
      current = [];
      currentH = 0;
      cap = MIDDLE_CAP;
    }
    current.push(items[i]);
    currentH += h;
  }
  if (current.length) pages.push(current);

  if (pages.length === 0) {
    pages.push([]);
    return pages;
  }

  const lastHeight = pages[pages.length - 1].reduce(
    (s, it) => s + estimateRowHeightPt(it),
    0
  );
  const capAvailable = pages.length === 1 ? FIRST_CAP + LAST_BLOCK : MIDDLE_CAP;
  if (lastHeight + LAST_BLOCK > capAvailable) {
    pages.push([]);
  }
  return pages;
};

const Header = ({ label, numberLabel, compact, logoSrc }) => (
  <View style={[styles.head, compact && styles.headCompact]}>
    <View style={styles.docId}>
      <Text style={styles.docLabel}>{'// DOCUMENT'}</Text>
      <Text style={compact ? styles.docBigCompact : styles.docBig}>
        {label.toLowerCase()}
      </Text>
      <View style={styles.docNumRow}>
        <View style={styles.docBullet} />
        <Text style={styles.docNum}>{numberLabel}</Text>
      </View>
    </View>
    {logoSrc && <Image src={logoSrc} style={styles.logo} />}
  </View>
);

const MetaRow = ({ cells }) => (
  <View style={styles.meta}>
    {cells.map((cell, idx) => {
      const isFirst = idx === 0;
      const isLast = idx === cells.length - 1;
      return (
        <View
          key={idx}
          style={[
            styles.metaCell,
            isFirst && styles.metaCellFirst,
            isLast && styles.metaCellLast,
          ]}
        >
          <Text style={styles.metaK}>{cell.k}</Text>
          <Text style={styles.metaV}>{cell.v}</Text>
        </View>
      );
    })}
  </View>
);

const Parties = ({ issuer, client, type }) => (
  <View style={styles.parties}>
    <View style={styles.party}>
      <Text style={styles.partyTag}>§01 · Prestataire</Text>
      <Text style={styles.partyName}>{issuer.name}</Text>
      <Text style={styles.partyLine}>
        {issuer.address}
        {'\n'}
        {issuer.city}
        {'\n'}
        {issuer.email}
        {'\n'}
        SIREN {issuer.siren}
      </Text>
    </View>
    <View style={styles.party}>
      <Text style={styles.partyTag}>§02 · Client</Text>
      <Text style={styles.partyName}>{client?.Title || ''}</Text>
      <Text style={styles.partyLine}>
        {client?.Adress || ''}
        {'\n'}
        {[client?.PostalCode, client?.Ville].filter(Boolean).join(' ')}
        {'\n'}
        France
      </Text>
    </View>
  </View>
);

const TableHeader = ({ middle }) => (
  <View style={[styles.tableHeader, middle && styles.tableHeaderMiddle]}>
    <Text style={[styles.th, styles.thIdx]}>N°</Text>
    <Text style={[styles.th, styles.thDesc]}>Prestation</Text>
    <Text style={[styles.th, styles.thQty]}>Qté</Text>
    <Text style={[styles.th, styles.thPrice]}>Prix HT</Text>
    <Text style={[styles.th, styles.thAmount]}>Montant</Text>
  </View>
);

const ItemRow = ({ item, index }) => {
  const price = parsePrice(item.unitPrice);
  const amount = (Number(item.quantity) || 0) * price;
  return (
    <View style={styles.row} wrap={false}>
      <Text style={styles.tdIdx}>{String(index + 1).padStart(2, '0')}</Text>
      <View style={styles.tdDesc}>
        <Text style={styles.tdTitle}>{itemLabel(item)}</Text>
        {item.description ? <Text style={styles.tdSub}>{item.description}</Text> : null}
      </View>
      <Text style={styles.tdQty}>
        {item.quantity}
        {item.unit ? <Text style={styles.tdQtyUnit}> {item.unit}</Text> : null}
      </Text>
      <Text style={styles.tdPrice}>{fmt(price)} €</Text>
      <Text style={styles.tdAmount}>{fmt(amount)} €</Text>
    </View>
  );
};

const CarryForward = ({ label, value }) => (
  <View style={styles.carryBottom}>
    <Text style={styles.carryLabel}>{label}</Text>
    <Text style={styles.carryValue}>{fmt(value)} €</Text>
  </View>
);

const ReportHeader = ({ pageIdx, cumulative }) => (
  <View style={styles.carryReport}>
    <Text style={styles.carryLabel}>
      Report page {String(pageIdx).padStart(2, '0')}
    </Text>
    <Text style={styles.carryValue}>{fmt(cumulative)} €</Text>
  </View>
);

const Totals = ({ totalHT }) => (
  <View style={styles.totals}>
    <View style={styles.totalsLeft}>
      <Text style={styles.totalsNotesTag}>{'// Notes'}</Text>
      <Text style={styles.totalsNotes}>
        TVA non applicable, art. 293 B du CGI.
        {'\n'}
        Dispensé d'immatriculation au RCS et au RM.
      </Text>
    </View>
    <View style={styles.totalsRight}>
      <View style={styles.totalsRow}>
        <Text style={styles.totalsK}>Sous-total HT</Text>
        <Text style={styles.totalsV}>{fmt(totalHT)} €</Text>
      </View>
      <View style={styles.totalsRow}>
        <Text style={styles.totalsK}>TVA (0%)</Text>
        <Text style={styles.totalsV}>—</Text>
      </View>
      <View style={styles.totalsGrand}>
        <Text style={styles.totalsGrandK}>
          {'Total à payer'}
        </Text>
        <Text style={styles.totalsGrandV}>{fmt(totalHT)} €</Text>
      </View>
    </View>
  </View>
);

const Payment = ({ issuer, reference }) => (
  <View style={styles.payment}>
    <Text style={styles.paymentTag}>§03 · Règlement par virement SEPA</Text>
    <View style={styles.paymentRow}>
      <Text style={styles.paymentK}>Bénéficiaire</Text>
      <Text style={styles.paymentV}>{issuer.name}</Text>
    </View>
    <View style={styles.paymentRow}>
      <Text style={styles.paymentK}>IBAN</Text>
      <Text style={styles.paymentV}>{issuer.iban}</Text>
    </View>
    <View style={styles.paymentRow}>
      <Text style={styles.paymentK}>BIC</Text>
      <Text style={styles.paymentV}>{issuer.bic}</Text>
    </View>
    <View style={styles.paymentRow}>
      <Text style={styles.paymentK}>Banque</Text>
      <Text style={styles.paymentV}>{issuer.bank}</Text>
    </View>
    <View style={styles.paymentRow}>
      <Text style={styles.paymentK}>Référence</Text>
      <Text style={styles.paymentV}>{reference}</Text>
    </View>
  </View>
);

const Validity = ({ validityDate }) => (
  <View style={styles.validity}>
    <Text style={styles.paymentTag}>§03 · Validité du devis</Text>
    <View style={styles.paymentRow}>
      <Text style={styles.paymentK}>Valable jusqu'au</Text>
      <Text style={styles.paymentV}>
        {validityDate ? formatDate(validityDate) : '30 jours à compter de l\'émission'}
      </Text>
    </View>
  </View>
);

const Footer = ({ issuer, pageN, total }) => (
  <View style={styles.footer} fixed>
    <Text style={styles.footerLegal}>
      {issuer.name} · {issuer.city} · SIRET {issuer.siret} · SIREN {issuer.siren} · TVA non applicable, art. 293 B du CGI · Dispensé d'immatriculation au RCS et au RM
    </Text>
    <Text style={styles.footerPage}>
      {String(pageN).padStart(2, '0')} / {String(total).padStart(2, '0')}
    </Text>
  </View>
);

const GridProtocolPDF = ({ invoice, client, emitter, type = 'invoice' }) => {
  const issuer = {
    name: emitter.name,
    address: emitter.address,
    city: emitter.city,
    email: emitter.email,
    siret: emitter.siret,
    siren: emitter.siren,
    iban: emitter.iban || 'FR76 4061 8804 4200 0409 1149 353',
    bic: emitter.bic || 'BOUSFRPPXXX',
    bank: emitter.bank || 'BoursoBank',
  };

  const label = type === 'invoice' ? 'FACTURE' : 'DEVIS';
  const prefix = type === 'invoice' ? 'INV' : 'Q';
  const numStr = String(invoice.num).padStart(4, '0');
  const year = parseDate(invoice.created || invoice.prestaDate).getFullYear();
  const numberLabel = `${prefix}-${year}-${numStr}`;
  const reference = numberLabel;

  const emitDate = formatDate(invoice.created || new Date());
  const prestaOrCreated =
    type === 'invoice'
      ? formatMonthYear(invoice.prestaDate)
      : formatMonthYear(invoice.created);
  const dueDate = invoice.dueDate
    ? formatDate(invoice.dueDate)
    : (() => {
        const d = parseDate(invoice.created || new Date());
        d.setDate(d.getDate() + 30);
        return d.toLocaleDateString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
      })();

  const products = invoice.products || [];
  const totalHT = products.reduce(
    (acc, it) => acc + (Number(it.quantity) || 0) * parsePrice(it.unitPrice),
    0
  );

  const pages = paginateItems(products);
  const totalPages = pages.length;

  let running = 0;
  const pageSubtotals = pages.map((rows) => {
    const sum = rows.reduce(
      (s, it) => s + (Number(it.quantity) || 0) * parsePrice(it.unitPrice),
      0
    );
    running += sum;
    return { sum, cumulative: running };
  });

  const metaCells =
    type === 'invoice'
      ? [
          { k: 'Émis le', v: emitDate },
          { k: 'Échéance', v: dueDate },
          { k: 'Prestation', v: prestaOrCreated },
          { k: 'SIRET', v: issuer.siret },
        ]
      : [
          { k: 'Émis le', v: emitDate },
          { k: 'Validité', v: invoice.validityDate ? formatDate(invoice.validityDate) : dueDate },
          { k: 'Création', v: prestaOrCreated },
          { k: 'SIRET', v: issuer.siret },
        ];

  return (
    <Document>
      {pages.map((rows, idx) => {
        const isFirst = idx === 0;
        const isLast = idx === pages.length - 1;
        const prevCumul = idx === 0 ? 0 : pageSubtotals[idx - 1].cumulative;
        const thisCumul = pageSubtotals[idx].cumulative;
        const startIndex = pages.slice(0, idx).reduce((s, p) => s + p.length, 0);

        return (
          <Page
            key={idx}
            size="A4"
            style={[styles.page, !isFirst && styles.pageMiddle]}
          >
            <Header
              label={label}
              numberLabel={numberLabel}
              compact={!isFirst}
              logoSrc="/logoMS.png"
            />

            {isFirst && <MetaRow cells={metaCells} />}
            {isFirst && <Parties issuer={issuer} client={client} type={type} />}

            {rows.length > 0 && (
              <>
                <TableHeader middle={!isFirst} />
                {!isFirst && <ReportHeader pageIdx={idx} cumulative={prevCumul} />}
                {rows.map((item, i) => (
                  <ItemRow
                    key={startIndex + i}
                    item={item}
                    index={startIndex + i}
                  />
                ))}
              </>
            )}

            {!isLast && (
              <CarryForward
                label="À reporter — page suivante"
                value={thisCumul}
              />
            )}

            {isLast && (
              <>
                <Totals totalHT={totalHT} />
                {type === 'invoice' ? (
                  <Payment issuer={issuer} reference={reference} />
                ) : (
                  <Validity validityDate={invoice.validityDate} />
                )}
              </>
            )}

            <Footer issuer={issuer} pageN={idx + 1} total={totalPages} />
          </Page>
        );
      })}
    </Document>
  );
};

export default GridProtocolPDF;
